import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import { cx } from '../utils/cx.js';
import { useOverlayFocusTrap } from '../utils/useOverlayFocusTrap.js';
import { isTopOverlay, popOverlay, pushOverlay } from '../utils/overlayEscStack.js';
import { overlayPresenceProps } from '../hooks/mistRail.js';
import { IconButton } from './IconButton.js';
import styles from './Drawer.module.css';

const DRAWER_MS = 220;

export type DrawerProps = {
  opened: boolean;
  onClose: () => void;
  /** Fires after close transition finishes (panel unmounted). */
  onExited?: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** `top` = sheet from top (full width). `left` / `right` = side panels. */
  placement?: 'right' | 'left' | 'top';
  /** Extra class on drawer panel (aside). */
  className?: string;
  /** Extra class on drawer header strip. */
  headerClassName?: string;
  /** Extra class on title element. */
  titleClassName?: string;
  /** Extra class on close IconButton. */
  closeClassName?: string;
  /** Extra class on drawer body (scrollable content area). */
  bodyClassName?: string;
  /** Slot before close control (e.g. maximize / restore). */
  headerActions?: ReactNode;
  /** Full-bleed strip under header row (e.g. wizard progress as bottom edge). */
  headerBottom?: ReactNode;
  /** Sticky chrome under body (mirrors header: mist fill + grain). */
  footer?: ReactNode;
  /** Extra class on drawer footer strip. */
  footerClassName?: string;
  /** Skip built-in header (caller owns title/close chrome). */
  hideHeader?: boolean;
  /** Dialog name when `hideHeader` (or no title). */
  'aria-label'?: string;
  /** Stable id for aria-controls from openers. */
  id?: string;
};

export function Drawer({
  opened,
  onClose,
  onExited,
  title,
  children,
  placement = 'right',
  className,
  headerClassName,
  titleClassName,
  closeClassName,
  bodyClassName,
  headerActions,
  headerBottom,
  footer,
  footerClassName,
  hideHeader = false,
  'aria-label': ariaLabel,
  id,
}: DrawerProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLElement>(null);
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;
  const [mounted, setMounted] = useState(opened);
  const [visible, setVisible] = useState(opened);

  useOverlayFocusTrap(mounted, panelRef);

  useEffect(() => {
    if (opened) {
      setMounted(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
  }, [opened]);

  useEffect(() => {
    if (!mounted || visible) return;
    const panel = panelRef.current;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setMounted(false);
      onExitedRef.current?.();
    };
    const onEnd = (e: TransitionEvent) => {
      if (e.target !== panel || e.propertyName !== 'transform') return;
      finish();
    };
    panel?.addEventListener('transitionend', onEnd);
    const fallback = window.setTimeout(finish, DRAWER_MS + 40);
    return () => {
      panel?.removeEventListener('transitionend', onEnd);
      window.clearTimeout(fallback);
    };
  }, [mounted, visible]);

  useEffect(() => {
    if (!mounted || !visible) return;
    const stackId = pushOverlay();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Nested overlays: only the top-most one closes on Escape.
      if (!isTopOverlay(stackId)) return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      popOverlay(stackId);
    };
  }, [mounted, visible, onClose]);

  if (!mounted) return null;

  return createPortal(
    <>
      <div
        className={cx(
          styles.backdrop,
          placement === 'top' && styles.backdropTop,
          visible && styles.backdropVisible,
        )}
        aria-hidden
        onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      />
      <aside
        ref={panelRef}
        id={id}
        className={cx(
          styles.panel,
          placement === 'top' ? styles.panelTop : placement === 'left' ? styles.panelLeft : styles.panelRight,
          visible && styles.panelVisible,
          className,
        )}
        aria-labelledby={title && !hideHeader ? titleId : undefined}
        aria-label={title && !hideHeader ? undefined : (ariaLabel ?? 'Panneau')}
        role="dialog"
        aria-modal="true"
        {...overlayPresenceProps(visible)}
      >
        {!hideHeader ? (
          <div
            className={cx(
              styles.header,
              !title && styles.headerOnlyClose,
              headerBottom != null && styles.headerWithBottom,
              headerClassName,
            )}
          >
            <div className={styles.headerMain}>
              {title ? (
                <h2 id={titleId} className={cx(styles.title, titleClassName)}>
                  {title}
                </h2>
              ) : null}
              {headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null}
              <IconButton
                type="button"
                variant="ghost"
                size="md"
                className={cx(styles.close, closeClassName)}
                aria-label="Fermer"
                onClick={onClose}
              >
                <IconX size={22} stroke={1.5} aria-hidden />
              </IconButton>
            </div>
            {headerBottom != null ? <div className={styles.headerBottom}>{headerBottom}</div> : null}
          </div>
        ) : null}
        <div
          className={cx(
            styles.body,
            styles.bodyInset,
            footer != null && styles.bodyWithFooter,
            bodyClassName,
          )}
        >
          {children}
        </div>
        {footer != null ? (
          <div className={cx(styles.footer, footerClassName)}>{footer}</div>
        ) : null}
      </aside>
    </>,
    document.body,
  );
}
